import { useRef, useState } from 'react'
import isEqual from 'lodash-es/isEqual'

import { EMPTY_LIST, EMPTY_OBJECT } from '../constants/common'
import { getApi, putApi } from '../utils/api'
import { identity } from '../utils/common'
import { getPermanentFilters as getPermanentFiltersCommon } from '../utils/filter'
import { toOrderingList } from '../utils/sort'

import { useIsMountedRef } from './useIsMountedRef'

/**
 * The {@link useFilterStateLoader} hook loads, holds and persists a table's
 * server-backed filter and ordering state.
 *
 * @param {object} params
 * @param {() => (Promise|void)} [params.beforeLoad] - Optional async side-effect awaited
 *    once, right before the server load (server-backed path only) — e.g. a search table
 *    resetting its persisted filter state on a fresh search. Its rejection is swallowed by
 *    the load's own catch. Defaults to undefined (no-op), so existing tables are unaffected.
 * @param {object} params.entity - Table entity; its `plural` builds the default endpoint.
 * @param {string} [params.filterStateApi] - Override endpoint for the filter state.
 * @param {Function} [params.getPermanentFilters] - Derives the always-applied permanent filters.
 * @param {(size: number) => void} [params.handleChangeSize] - Receives the loaded page size.
 * @param {boolean} [params.isLocal] - Keep the filter/ordering state in memory only,
 *    without loading from or persisting to the server (replaces the former
 *    `useLocalFilterState`).
 * @param {object} [params.pagination] - Current pagination, sent when persisting state.
 * @param {object} [params.saveExtras] - Extra fields merged into every persist (PUT) body —
 *    e.g. a sibling `search_query` for OpenSearch-backed search tables. Defaults to an empty
 *    object, so existing tables are unaffected.
 * @param {string} params.sub - Filter-state sub-key (namespace) used in the endpoint.
 * @param {(filters: object) => object} [params.transformInitialFilters] - Maps the
 *    filters right after they load from the server, before they are stored locally —
 *    e.g. to apply a URL-derived sub-tab for a deep link. This step itself does not
 *    write back to the server, but the transformed values become part of the local
 *    filter state and so may be persisted later by any subsequent save action
 *    (e.g. a page-size or ordering change). Defaults to identity, so existing tables
 *    are unaffected.
 * @param {(filters: object|null) => object} [params.transformSaveFilters] - The mirror of
 *    `transformInitialFilters`: maps the filters on the way out, on every persist
 *    (ordering, page size, filter apply, reset). Lets a table whose backend schema
 *    requires filters the UI owns outside the filter form (e.g. the documents
 *    `parent` folder) inject them in one place instead of per handler. Receives
 *    `null` before the first load. Defaults to identity, so existing tables are
 *    unaffected.
 *
 * @returns {object} The filter/ordering state and its handlers.
 */
export function useFilterStateLoader({
  beforeLoad,
  entity,
  filterStateApi,
  getPermanentFilters = getPermanentFiltersCommon,
  handleChangeSize,
  isLocal = false,
  pagination,
  saveExtras = EMPTY_OBJECT,
  sub,
  transformInitialFilters = identity,
  transformSaveFilters = identity,
}) {
  const isMountedRef = useIsMountedRef()

  const loadGenerationRef = useRef(0)

  const [ordering, setOrdering] = useState(null)
  const [filterValues, setFilterValues] = useState(null)

  const permanentFilters = getPermanentFilters({
    entityType: entity?.plural,
    filterValues: isLocal ? undefined : filterValues,
    sub,
  })

  const hasFilterChanged = !isEqual(filterValues, permanentFilters)

  async function loadServerFilterState() {
    loadGenerationRef.current += 1

    const generation = loadGenerationRef.current

    await beforeLoad?.()

    const { data } = await getApi({
      endpoint:
        filterStateApi ||
        `idwell-api/api/v3/table/filter-states/${entity.plural}/${sub}/`,
    })

    if (
      isMountedRef.current !== false &&
      generation === loadGenerationRef.current
    ) {
      setFilterValues(transformInitialFilters(data.filters))

      setOrdering(toOrderingList(data.ordering))

      handleChangeSize?.(data?.pagination.per_page)

      return data
    }
  }

  async function handleLoadFilterState() {
    if (isLocal) {
      setFilterValues(permanentFilters)
      setOrdering(EMPTY_LIST)

      return {
        filters: permanentFilters,
        ordering: EMPTY_LIST,
        pagination: EMPTY_OBJECT,
      }
    }

    return loadServerFilterState().catch(error => console.log(error))
  }

  // Keep object fallbacks out of parameter defaults so the React Compiler can memoize this hook.
  async function handleSaveTableState({
    filters: nextFilters = filterValues,
    ordering: nextOrdering = ordering,
    pagination: nextPagination,
  } = EMPTY_OBJECT) {
    return putApi({
      data: {
        ...saveExtras,
        filters: transformSaveFilters(nextFilters),
        ordering: toOrderingList(nextOrdering),
        pagination: nextPagination ?? { per_page: pagination?.pageSize },
      },
      endpoint:
        filterStateApi ||
        `idwell-api/api/v3/table/filter-states/${entity.plural}/${sub}/`,
    })
  }

  async function handleSetOrdering(nextOrdering) {
    setOrdering(nextOrdering)

    if (isLocal) {
      return nextOrdering
    }

    return handleSaveTableState({ ordering: nextOrdering })
  }

  async function handleResetOrdering() {
    return handleSetOrdering(EMPTY_LIST)
  }

  async function handleSetFilterValues(nextFilterValues) {
    if (isLocal) {
      setFilterValues(nextFilterValues)

      return nextFilterValues
    }

    const generation = loadGenerationRef.current

    // Actualize data from API, cause in `filterValues` could be invalid values
    const { data } = await handleSaveTableState({ filters: nextFilterValues })

    // A save that started before a newer load (e.g. on the previous sub-tab) must not overwrite it.
    if (
      isMountedRef.current !== false &&
      generation === loadGenerationRef.current
    ) {
      setFilterValues(data.filters)
    }

    return data
  }

  async function handleResetFilterValues() {
    if (isLocal) {
      setFilterValues(permanentFilters)

      return permanentFilters
    }

    return handleSetFilterValues(EMPTY_OBJECT)
  }

  async function handleSetSize(size) {
    handleChangeSize?.(size)

    if (isLocal) {
      return size
    }

    return handleSaveTableState({ pagination: { per_page: size } })
  }

  return {
    filterValues,
    handleLoadFilterState,
    handleResetFilterValues,
    handleResetOrdering,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetSize,
    hasFilterChanged,
    ordering,
  }
}
